import classNames from 'classnames';

import { Country, State, Stop } from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import Button from '@/components/global/buttons/button';
import Select from '@/components/global/forms/select';

export const renderAlphabets = (
  label: string,
  ariaLabel: string,
  instructions: string,
  alphabets: string[],
  alphabet: string,
  alphabetButtonRefs: any,
  focusedIndex: number,
  setFocusedIndex: (focusedIndex: number) => void,
  initialRender: boolean,
  setInitialRender: (initialRender: boolean) => void,
  setAlphabet: (alphabet: string) => void,
  styles: {
    readonly [key: string]: string;
  },
  lng: string
) => {
  const { t } = doTranslation(lng);
  const options = [{ label: t('All'), value: 'ALL' }].concat(
    alphabets.map((a: string) => {
      return { label: a, value: a };
    })
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight') {
      if (index === 0) {
        setFocusedIndex(1);
        alphabetButtonRefs.current[1]?.focus();
      } else if (index < alphabetButtonRefs.current.length) {
        setFocusedIndex(index);
        alphabetButtonRefs.current[index]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && focusedIndex != 0) {
      setFocusedIndex(focusedIndex - 1);
      alphabetButtonRefs.current[focusedIndex - 1]?.focus();
    }
  };

  return (
    <div className={styles.alphabets}>
      <div className="visually-hidden" id="filter-letters-instructions">
        {instructions}
      </div>
      <ul aria-label={ariaLabel} aria-describedby="filter-letters-instructions" className={styles.ul}>
        <li className={styles.li}>
          <button
            ref={(el) => {
              alphabetButtonRefs.current[0] = el;
            }}
            className={classNames({ [styles.button]: true, [styles.active]: alphabet === 'ALL' && !initialRender })}
            onClick={() => {
              setInitialRender(false);
              setFocusedIndex(0);
              setAlphabet('ALL');
            }}
            onKeyDown={(e) => handleKeyDown(e, 0)}
          >
            {t('ALL')}
          </button>
        </li>
        {alphabets.map((a, index) => {
          return (
            <li className={styles.li} key={`${a}`}>
              <button
                ref={(el) => {
                  alphabetButtonRefs.current[index + 1] = el;
                }}
                className={classNames({ [styles.button]: true, [styles.active]: alphabet === a })}
                onKeyDown={(e) => handleKeyDown(e, index + 2)}
                onClick={() => {
                  setFocusedIndex(index + 1);
                  setAlphabet(a);
                }}
                tabIndex={-1}
              >
                {a}
              </button>
            </li>
          );
        })}
      </ul>
      <div className={styles.filter}>
        <div className={styles.field}>
          <Select
            label={label}
            defaultValue={options.find((o: any) => o.value === alphabet)}
            inputId="alphabet"
            instanceId="alphabet"
            isSearchable={false}
            name="alphabet"
            options={options}
          />
        </div>
        <Button
          secondary
          onClick={() => {
            setAlphabet(document.querySelector('[name=alphabet]')?.getAttribute('value') || 'ALL');
          }}
        >
          {t('Apply Filter')}
        </Button>
      </div>
    </div>
  );
};

export const renderBoxes = (
  alphabets: string[],
  alphabet: string,
  alphabetButtonRefs: any,
  caption: string,
  ariaIDCaption: string,
  items: State[] | Stop[],
  item: string,
  focusedIndex: number,
  styles: {
    readonly [key: string]: string;
  },
  lng: string,
  onClick: (s: string) => void
) => {
  const { t } = doTranslation(lng);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number, j: number) => {
    if (e.key === 'Tab' && j === 0 && e.shiftKey && (i === 0 || ('ALL' != alphabet && i === focusedIndex - 1))) {
      e.preventDefault();
      alphabetButtonRefs.current[focusedIndex]?.focus();
    }
  };
  items = items.sort((a, b) => a.label.localeCompare(b.label));
  return alphabets.map((a, i) => {
    if (!('ALL' === alphabet || a === alphabet)) {
      return null;
    }
    return (
      <div className={styles.box} key={a}>
        <span className={styles.alphabet} id={t(ariaIDCaption, { alphabet: a })}>
          <span className="visually-hidden">{t(caption, { alphabet: a })}</span>
          <span aria-hidden="true">{a}</span>
        </span>
        <ul
          aria-describedby="selection-instructions"
          aria-labelledby={`${t(ariaIDCaption, { alphabet: a })}`}
          className={styles.ul}
        >
          {items
            .filter((s: any) => s.alphabet === a)
            .map((s: any, j: number) => {
              return (
                <li className={styles.li} key={`${s.value}`}>
                  <button
                    className={classNames({
                      [styles.button]: true,
                      [styles.active]: item === s.value
                    })}
                    onClick={() => {
                      onClick(s.value);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, i, j)}
                  >
                    {s.label}
                  </button>
                </li>
              );
            })}
        </ul>
      </div>
    );
  });
};

export const renderCountry = (
  countries: Country[],
  country: Country,
  styles: {
    readonly [key: string]: string;
  },
  routeSlug: string,
  countrySelectRef: any,
  lng: string,
  onChange: (selectedOption: any) => void,
  formatOptionLabel: (props: any) => React.JSX.Element
) => {
  const { t } = doTranslation(lng);
  return (
    <div className={styles.fields}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${routeSlug}-country`}>
          {t('Country')}
        </label>
        <Select
          reference={countrySelectRef}
          defaultValue={country}
          formatOptionLabel={formatOptionLabel}
          inputId={`${routeSlug}-country`}
          instanceId={`${routeSlug}-country`}
          isSearchable={false}
          name="country"
          id="country"
          onChange={onChange}
          options={countries}
        />
      </div>
    </div>
  );
};

export const renderState = (
  country: Country,
  state: State,
  styles: {
    readonly [key: string]: string;
  },
  routeSlug: string,
  lng: string,
  onChange: (selectedOption: any) => void
) => {
  const { t } = doTranslation(lng);
  return (
    <div className={styles.fields}>
      <div className={styles.field} id="locations-state-value">
        <label className={styles.label} htmlFor={`${routeSlug}-state`}>
          {country.value === 'ca' ? t('Province') : t('State')}
        </label>
        <Select
          defaultValue={state}
          inputId={`${routeSlug}-state`}
          instanceId={`${routeSlug}-state`}
          isSearchable={false}
          name="state"
          onChange={onChange}
          options={country.states}
        />
      </div>
    </div>
  );
};
